// Pure grouping helpers for the org process monitor, extracted from
// ProcessMonitorButton so the roll-up logic is unit-testable without rendering
// the component (which pulls a modal/confirm/API). Mirrors the pages/workflowGraph.js
// "extract pure logic for tests" pattern.

/**
 * Parse a fan-out job label ("17/81") to its 1-based track index (17). Used to
 * order sibling tracks within a group. Returns Infinity when there's no parseable
 * index, so unlabeled/odd jobs sort to the end but never throw.
 */
export function jobLabelIndex(jobLabel) {
  if (!jobLabel) return Infinity;
  const m = String(jobLabel).match(/(\d+)\s*\/\s*\d+/);
  if (m) return Number(m[1]);
  const n = Number(String(jobLabel).trim());
  return Number.isFinite(n) ? n : Infinity;
}

/**
 * Collapse fan-out siblings into per-tile groups. Siblings of one fan-out (e.g. an
 * AM study's 81 tracks) share ownerUserId + projectId + nodeId + stage; nodeId is a
 * global UUID, so this key never merges two different tiles. A job with no siblings
 * yields a group of one (rendered as a normal row).
 *
 * Bucketing preserves the backend's job order (running-first, then enqueue time)
 * via Map insertion order; groups are then stable-sorted mine-first, matching the
 * previous flat-list ordering. Within a group, jobs are ordered by track index.
 *
 * @returns ordered array of
 *   { key, jobs, ownerUserId, displayName, projectId, projectName, nodeId,
 *     nodeLabel, stage, resourceType, mine, runningCount, queuedCount }
 */
export function groupJobsByTile(jobs, currentUserId) {
  const buckets = new Map();
  for (const j of jobs || []) {
    const key = `${j.ownerUserId}::${j.projectId}::${j.nodeId}::${j.stage}`;
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(j);
  }

  const groups = [];
  for (const [key, groupJobs] of buckets) {
    const sortedJobs = [...groupJobs].sort(
      (a, b) => jobLabelIndex(a.jobLabel) - jobLabelIndex(b.jobLabel)
    );
    const first = sortedJobs[0];
    groups.push({
      key,
      jobs: sortedJobs,
      ownerUserId: first.ownerUserId,
      displayName: first.displayName,
      projectId: first.projectId,
      projectName: first.projectName,
      nodeId: first.nodeId,
      nodeLabel: first.nodeLabel,
      stage: first.stage,
      resourceType: first.resourceType,
      mine: first.ownerUserId === currentUserId,
      runningCount: sortedJobs.filter((j) => j.status === 'running').length,
      queuedCount: sortedJobs.filter((j) => j.status !== 'running').length,
    });
  }

  // Stable sort: the caller's own groups first (same comparator as the old flat list).
  return groups.sort((a, b) => (a.mine ? 0 : 1) - (b.mine ? 0 : 1));
}
